/**
 * OFPilot - Branding Settings Page
 * =================================
 * Allows CFA Admin to customize organization branding
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Palette, 
  Upload, 
  Image, 
  RefreshCw, 
  Save,
  Trash2,
  Eye,
  Building2,
  Mail
} from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';
import api from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BrandingSettingsPage = () => {
  const { refreshBranding, organizationName, logoUrl, primaryColor, secondaryColor, emailSignatureName } = useBranding();
  
  const [settings, setSettings] = useState({
    primaryColor: primaryColor || '#6366F1',
    secondaryColor: secondaryColor || '#8B5CF6',
    emailSignatureName: emailSignatureName || ''
  });
  const [currentLogo, setCurrentLogo] = useState(logoUrl);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  
  const logoInputRef = useRef(null);

  useEffect(() => {
    setSettings({
      primaryColor: primaryColor || '#6366F1',
      secondaryColor: secondaryColor || '#8B5CF6',
      emailSignatureName: emailSignatureName || organizationName || ''
    });
    setCurrentLogo(logoUrl);
  }, [primaryColor, secondaryColor, emailSignatureName, logoUrl, organizationName]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/branding/', {
        primary_color: settings.primaryColor,
        secondary_color: settings.secondaryColor,
        email_signature_name: settings.emailSignatureName
      });
      toast.success('Paramètres de branding enregistrés');
      refreshBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5MB)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/branding/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCurrentLogo(response.data.logo_url);
      toast.success('Logo téléchargé avec succès');
      refreshBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Supprimer le logo de l\'organisation ?')) return;

    try {
      await api.delete('/branding/logo');
      setCurrentLogo(null);
      toast.success('Logo supprimé');
      refreshBranding();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResetBranding = async () => {
    if (!window.confirm('Réinitialiser le branding aux valeurs par défaut OFPilot ?')) return;

    try {
      await api.post('/branding/reset');
      toast.success('Branding réinitialisé');
      refreshBranding();
      setSettings({
        primaryColor: '#6366F1',
        secondaryColor: '#8B5CF6',
        emailSignatureName: organizationName || ''
      });
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  return (
    <div className="space-y-6" data-testid="branding-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Palette className="w-7 h-7 text-indigo-600" />
            Branding Organisation
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Personnalisez l'apparence de la plateforme pour {organizationName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPreviewMode(!previewMode)}
            className="gap-2"
          >
            <Eye className="w-4 h-4" />
            {previewMode ? 'Masquer aperçu' : 'Aperçu'}
          </Button>
          <Button
            variant="outline"
            onClick={handleResetBranding}
            className="gap-2 text-amber-600 hover:text-amber-700"
          >
            <RefreshCw className="w-4 h-4" />
            Réinitialiser
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Logo de l'organisation
            </CardTitle>
            <CardDescription>
              Ce logo apparaîtra dans le header du dashboard, les emails et les exports PDF
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Logo Display */}
            <div className="flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700">
              {currentLogo ? (
                <div className="relative group">
                  <img
                    src={currentLogo.startsWith('http') ? currentLogo : `${API_URL}${currentLogo}`}
                    alt="Logo organisation"
                    className="max-h-32 max-w-full object-contain"
                    data-testid="org-logo-preview"
                  />
                  <button
                    onClick={handleDeleteLogo}
                    className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Supprimer le logo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center text-slate-400">
                  <Building2 className="w-16 h-16 mx-auto mb-2 opacity-50" />
                  <p>Aucun logo configuré</p>
                  <p className="text-xs">Le branding OFPilot sera utilisé</p>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <input
              type="file"
              ref={logoInputRef}
              onChange={handleLogoUpload}
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
            />
            <Button
              onClick={() => logoInputRef.current?.click()}
              disabled={uploading}
              className="w-full gap-2"
              style={{ backgroundColor: settings.primaryColor }}
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Téléchargement...' : 'Télécharger un logo'}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Formats acceptés : PNG, JPG, SVG, WebP (max 5MB)
            </p>
          </CardContent>
        </Card>

        {/* Color Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Couleurs
            </CardTitle>
            <CardDescription>
              Ces couleurs s'appliqueront aux boutons et éléments interactifs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Primary Color */}
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Couleur principale</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="primaryColor"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                  className="w-12 h-12 rounded-lg cursor-pointer border-0"
                />
                <Input
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                  placeholder="#6366F1"
                  className="font-mono"
                  data-testid="primary-color-input"
                />
              </div>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <Label htmlFor="secondaryColor">Couleur secondaire</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="secondaryColor"
                  value={settings.secondaryColor}
                  onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                  className="w-12 h-12 rounded-lg cursor-pointer border-0"
                />
                <Input
                  value={settings.secondaryColor}
                  onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                  placeholder="#8B5CF6"
                  className="font-mono"
                  data-testid="secondary-color-input"
                />
              </div>
            </div>

            {/* Color Preview */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Aperçu des couleurs :</p>
              <div className="flex gap-2">
                <button
                  className="px-4 py-2 rounded-lg text-white font-medium transition-colors"
                  style={{ backgroundColor: settings.primaryColor }}
                >
                  Bouton principal
                </button>
                <button
                  className="px-4 py-2 rounded-lg text-white font-medium transition-colors"
                  style={{ backgroundColor: settings.secondaryColor }}
                >
                  Bouton secondaire
                </button>
              </div>
              <div 
                className="h-2 rounded-full"
                style={{ 
                  background: `linear-gradient(to right, ${settings.primaryColor}, ${settings.secondaryColor})` 
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Email Signature Settings */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Signature Email
            </CardTitle>
            <CardDescription>
              Le nom qui apparaîtra dans la signature des emails envoyés par la plateforme
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emailSignatureName">Nom de signature</Label>
              <Input
                id="emailSignatureName"
                value={settings.emailSignatureName}
                onChange={(e) => setSettings({ ...settings, emailSignatureName: e.target.value })}
                placeholder={organizationName}
                data-testid="email-signature-input"
              />
              <p className="text-xs text-slate-500">
                Par défaut : {organizationName}
              </p>
            </div>

            {/* Email Preview */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Aperçu signature email :</p>
              <div className="p-3 bg-white dark:bg-slate-900 rounded border text-sm">
                <p className="font-medium">{settings.emailSignatureName || organizationName}</p>
                <p className="text-slate-500 text-xs mt-1">via OFPilot</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          size="lg"
          className="gap-2"
          style={{ backgroundColor: settings.primaryColor }}
          data-testid="save-branding-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>

      {/* Preview Section */}
      {previewMode && (
        <Card className="border-2 border-dashed border-indigo-200 dark:border-indigo-800">
          <CardHeader>
            <CardTitle>Aperçu du branding</CardTitle>
            <CardDescription>Voici comment votre branding apparaîtra dans la plateforme</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dashboard Header Preview */}
            <div>
              <p className="text-sm font-medium mb-2 text-slate-500">Header Dashboard :</p>
              <div className="flex items-center gap-4 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                {currentLogo ? (
                  <img
                    src={currentLogo.startsWith('http') ? currentLogo : `${API_URL}${currentLogo}`}
                    alt="Logo"
                    className="h-10 object-contain"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: settings.primaryColor }}>
                    {organizationName?.charAt(0) || 'O'}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold">{organizationName}</h3>
                  <p className="text-xs text-slate-500">Centre de formation</p>
                </div>
              </div>
            </div>

            {/* Footer Preview */}
            <div>
              <p className="text-sm font-medium mb-2 text-slate-500">Footer :</p>
              <div className="p-4 bg-slate-900 text-white rounded-lg text-center">
                <p className="text-sm text-slate-400">Powered by <span className="font-semibold text-white">OFPilot</span></p>
              </div>
            </div>

            {/* Email Preview */}
            <div>
              <p className="text-sm font-medium mb-2 text-slate-500">Email Footer :</p>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border">
                <div className="text-center text-sm">
                  <p className="text-slate-600 dark:text-slate-400">
                    Cet email a été envoyé automatiquement par <strong>{settings.emailSignatureName || organizationName}</strong>
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Envoyé via la plateforme OFPilot
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
        <h4 className="font-medium text-indigo-900 dark:text-indigo-100 mb-2">
          ℹ️ À propos du branding
        </h4>
        <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1">
          <li>• Le logo OFPilot reste visible dans la sidebar principale</li>
          <li>• Votre logo apparaît dans le header du dashboard, les emails et les exports PDF</li>
          <li>• Les couleurs s'appliquent aux boutons et éléments interactifs</li>
          <li>• Le footer affiche toujours "Powered by OFPilot"</li>
        </ul>
      </div>
    </div>
  );
};

export default BrandingSettingsPage;
